import {Box, Tooltip} from "@material-ui/core";
import Divider from "@material-ui/core/Divider";
import ListAltOutlinedIcon from "@material-ui/icons/ListAltOutlined";
import MenuBookIcon from "@material-ui/icons/MenuBook";
import SettingsIcon from "@material-ui/icons/Settings";
import EmailIcon from "@material-ui/icons/Email";
import {
  useCurrentShow,
  useIsOrganiserCompanyAdminVendr,
  useUserConversations,
} from "hooks";
import useTaskReviewCount from "hooks/useTaskReviewCount";
import {useDispatch} from "react-redux";
import {
  IOrganiser,
  OrganiserToShowRole,
  PermissionPackage,
  ShowExhibitorChatMode,
} from "@evennt-tech/api-v2-core";
import React, {FC, useCallback, useEffect, useState} from "react";

import {
  generatePath,
  matchPath,
  useLocation,
  useParams,
} from "react-router-dom";
import {resetExhibitorsImportProgress} from "store/organisers/bulkUpload/slice";
import useStyles from "view/components/SideBar/style";
import GroupIcon from "@material-ui/icons/Group";
import {
  AccessIcon,
  BackButtonIcon,
  CustomReportIcon,
  DashboardIcon,
  ExhibitorsIcon,
  OnlineManualIcon,
  OrdersIcon,
  ShoppingIcon,
  SuppliersIcon,
  TaskReviewIcon,
} from "view/images/navigationIcons";
import {
  APP_PATH,
  LIBRARIES_PATH,
  SETTINGS_PATH,
  SHOW_ACCESS_PATH,
  SHOW_CUSTOM_REPORTS_PATH,
  SHOW_DASHBOARD_PATH,
  SHOW_EXHIBITORS_PATH,
  SHOW_INFO_PATH,
  SHOW_MESSAGING_BOARD,
  SHOW_ONLINE_MANUAL_PATH,
  SHOW_ORDERS_PATH,
  SHOW_ORGANISER_SHOPPING_PATH,
  SHOW_SETTINGS_PATH,
  SHOW_SUPPLIERS_PATH,
  SHOW_TASK_REVIEW_PATH,
  SHOWS_PATH,
  SUPPLIERS_LIST_PATH,
  USERS_PATH,
} from "view/routes";
import {selectUser} from "api/firebase";

import {OrganiserCompanyToOrganiserRole} from "@evennt-tech/api-v2-core/dist/organiser-company/enums";
import {useOrganisers} from "store/organisers/organiser.store";
import DisabledShopIcon from "view/components/DisabledShopIcon";
import MenuBookOutlinedIcon from "@material-ui/icons/MenuBookOutlined";
import NavItem, {NavList} from "./Nav";

type ShowNavigationProps = {
  organiser: IOrganiser;
  showId: string;
};

const ShowNavigation: FC<ShowNavigationProps> = ({showId, organiser}) => {
  const classes = useStyles({});
  const [show] = useCurrentShow();

  // Sidebar task-review badge: a dedicated count-only query (no rows
  // fetched). Counts in-flight items the organiser must action —
  // status IN (inReview, WFA), approval not notRequired/email, and this
  // organiser has not yet decided — computed server-side.
  const {count: pendingTaskReviewCount} = useTaskReviewCount(show?.id);

  const [role, setRole] = useState<string | null>(null);
  const [loadingRole, setLoadingRole] = useState<boolean>(true);

  const getOrganiserShowRole = useOrganisers((s) => s.getOrganiserShowRole);

  useEffect(() => {
    const fetchRole = async () => {
      if (!organiser?.id || !showId) return;

      try {
        const result = await getOrganiserShowRole({
          showId,
          organiserId: organiser.id,
        });
        setRole(result);
      } catch (e) {
        console.error("Failed to load organiser role", e);
        setRole(null);
      } finally {
        setLoadingRole(false);
      }
    };

    fetchRole();
  }, [organiser?.id, showId]);

  const {conversations, loading} = useUserConversations(showId, "organisers");

  const dispatch = useDispatch();

  const calculateMessagingBoardBadge = async () => {
    let count = 0;
    if (conversations?.organisers && !loading) {
      const organisersConversations = conversations.organisers;
      count = Object.values(organisersConversations).reduce(
        (sum: number, conversation: any) => {
          return sum + ((conversation.unreadCount as number) || 0);
        },
        0
      );
    }

    return count > 0 ? count : 0;
  };

  // Stable reference (changes only when the underlying count or the
  // TASKD-gate changes). `BadgeAlert` re-invokes whenever the prop
  // identity changes, so without `useCallback` the badge calculation
  // would re-run on every parent render — including transient frames
  // where react-query had not yet hydrated `inboxBadge.items`,
  // producing the "appear then disappear" flash the user reported.
  const isTaskdIncluded = !!(
    show && show.permissionPackages?.includes(PermissionPackage.TASKD)
  );
  const calculateTaskReviewBadge = useCallback(async () => {
    if (!show || !isTaskdIncluded) return 0;
    return pendingTaskReviewCount;
  }, [show, isTaskdIncluded, pendingTaskReviewCount]);

  const renderShowMenu = () => {
    if (!show) {
      return null;
    }

    const organiserPackages = organiser?.permissionPackages ?? [];
    const showPackages = show?.permissionPackages ?? [];

    const hasAccessToPackage = (pkg: PermissionPackage) => {
      const showHas = showPackages.includes(pkg);
      const organiserHas = organiserPackages.includes(pkg);
      return showHas && organiserHas;
    };

    const renderAdminRoutes = () => {
      if (loadingRole || role !== OrganiserToShowRole.SHOW_ADMIN) {
        return null;
      }

      return (
        <>
          <Divider />
          <NavList>
            <NavItem
              icon={AccessIcon}
              name="Manage Access"
              to={generatePath(SHOW_ACCESS_PATH, {showId})}
            />
            <NavItem
              id="setting-left-bar-button"
              icon={SettingsIcon}
              name="Settings"
              to={generatePath(SHOW_SETTINGS_PATH, {showId})}
            />
          </NavList>
        </>
      );
    };

    const renderTaskdRoutes = () => {
      if (!hasAccessToPackage(PermissionPackage.TASKD)) return null;

      return (
        <>
          <Divider />
          <NavList>
            <NavItem
              id="tasks-left-bar-button"
              icon={OnlineManualIcon}
              name="Tasks"
              to={generatePath(SHOW_ONLINE_MANUAL_PATH, {showId})}
            />
            {show?.exhibitorChatMode ===
              ShowExhibitorChatMode.MessagingBoard && (
              <NavItem
                icon={EmailIcon}
                name="Messaging Board"
                to={generatePath(SHOW_MESSAGING_BOARD, {showId})}
                calculateBadgeCount={calculateMessagingBoardBadge}
              />
            )}
            <NavItem
              icon={MenuBookIcon}
              name="Show Info"
              to={generatePath(SHOW_INFO_PATH, {showId})}
            />
            <NavItem
              icon={TaskReviewIcon}
              name="Tasks Review"
              to={generatePath(SHOW_TASK_REVIEW_PATH, {showId})}
              calculateBadgeCount={calculateTaskReviewBadge}
            />
          </NavList>
        </>
      );
    };

    const renderVendrRoutes = () => {
      if (!hasAccessToPackage(PermissionPackage.VENDR)) return null;

      const shopDisabledIcon = !show.isShopEnabled ? (
        <DisabledShopIcon tooltipTitle="Disabled for the Show" />
      ) : null;

      return (
        <>
          <Divider />
          <NavList>
            <NavItem
              icon={OrdersIcon}
              name="Orders"
              to={generatePath(SHOW_ORDERS_PATH, {showId})}
              endIcon={shopDisabledIcon}
            />
            <NavItem
              icon={SuppliersIcon}
              name="Suppliers"
              to={generatePath(SHOW_SUPPLIERS_PATH, {showId})}
              endIcon={shopDisabledIcon}
            />
            <NavItem
              icon={ShoppingIcon}
              name="Exhibitor Shop"
              to={generatePath(SHOW_ORGANISER_SHOPPING_PATH, {showId})}
              endIcon={shopDisabledIcon}
            />
          </NavList>
        </>
      );
    };
    return (
      <>
        <Divider />
        <div className={classes.name}>{show.name}</div>
        <NavList>
          <NavItem
            icon={DashboardIcon}
            name="Dashboard"
            to={generatePath(SHOW_DASHBOARD_PATH, {showId})}
          />
          <NavItem
            id="exhibitors-left-bar-button"
            icon={ExhibitorsIcon}
            name="Exhibitors"
            to={generatePath(SHOW_EXHIBITORS_PATH, {showId})}
          />
          <NavItem
            icon={CustomReportIcon}
            name="Custom reports"
            to={generatePath(SHOW_CUSTOM_REPORTS_PATH, {showId})}
          />
        </NavList>
        {renderTaskdRoutes()}
        {renderVendrRoutes()}
        {renderAdminRoutes()}
      </>
    );
  };

  return (
    <>
      <NavList>
        <NavItem
          onClick={() => dispatch(resetExhibitorsImportProgress())}
          icon={BackButtonIcon}
          name="Back to all shows"
          to={SHOWS_PATH}
        />
      </NavList>
      {renderShowMenu()}
    </>
  );
};
const OrganiserNavigation: FC = () => {
  const userId = selectUser()?.id;

  const {showId} = useParams();

  const {selected: organiser, fetchOneByUserId} = useOrganisers();
  const {pathname} = useLocation();
  const isLibrariesSection = !!matchPath(
    {path: LIBRARIES_PATH, end: false},
    pathname
  );

  const isOrganiserAdminWithVendr = useIsOrganiserCompanyAdminVendr(organiser);

  useEffect(() => {
    if (userId) {
      fetchOneByUserId({userId});
    }
  }, [userId]);

  if (!organiser) {
    return null;
  }

  if (showId) {
    return <ShowNavigation showId={showId} organiser={organiser} />;
  }

  return (
    <Box>
      <NavList>
        <NavItem to={APP_PATH} icon={ListAltOutlinedIcon} name="Shows" />
        {isOrganiserAdminWithVendr ? (
          <NavItem
            to={SUPPLIERS_LIST_PATH}
            icon={SuppliersIcon}
            name="Suppliers"
          />
        ) : (
          <></>
        )}
        {organiser?.organiserCompanyToOrganisers?.[0]?.role ===
          OrganiserCompanyToOrganiserRole.ADMIN && (
          <>
            <NavItem to={USERS_PATH} icon={GroupIcon} name="Users" />
            <NavItem
              to={LIBRARIES_PATH}
              icon={MenuBookOutlinedIcon}
              name="Libraries"
              // `NavItem` only lights up on an exact path, so the library's own
              // sub-pages — the Show Info preview opens at
              // `/libraries/show-info/preview` — would leave the whole menu
              // looking unselected. `end: false` matches the section, not the page.
              selected={isLibrariesSection}
            />
          </>
        )}
      </NavList>
      {organiser?.organiserCompanyToOrganisers?.[0]?.role ===
        OrganiserCompanyToOrganiserRole.ADMIN && (
        <>
          <Divider />
          <NavList>
            <NavItem to={SETTINGS_PATH} icon={SettingsIcon} name="Settings" />
          </NavList>
        </>
      )}
    </Box>
  );
};

export default OrganiserNavigation;
